import { useEffect, useRef, useState } from "react";
import { isConsentGranted, usePrivacyPreferences } from "../privacy/privacyPreferences";

const VOICE_CONSENT_REQUIRED_MESSAGE = "Enable Voice SI in Privacy before using voice.";

export function useVoiceMicrophoneController({
  isMobile,
  browserSupportsSpeechRecognition,
  browserSupportsContinuousListening,
  isMicrophoneAvailable,
  detectImmediateMobileCommand,
  apiFetch,
  apiUrl,
  SpeechRecognition,
  micEnabled,
}) {
  const privacyPreferences = usePrivacyPreferences();
  const onlineFeaturesEnabled = privacyPreferences.scopes.voice === true &&
    !privacyPreferences.pendingScopes?.includes("voice");
  const nativeSpeechRecognitionSupported =
    typeof window !== "undefined" &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const mobileRecorderSupported =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  const speechRecognitionSupported =
    browserSupportsSpeechRecognition || nativeSpeechRecognitionSupported;
  const micInputSupported =
    speechRecognitionSupported;

  const nativeRecognitionRef = useRef(null);
  const nativeRestartTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const micEnabledRef = useRef(false);
  const mountedRef = useRef(true);
  const hasVoiceSIBeenEnabledRef = useRef(false);

  const [nativeTranscript, setNativeTranscript] = useState("");
  const [nativeListening, setNativeListening] = useState(false);
  const [micError, setMicError] = useState("");

  useEffect(() => {
    if (!onlineFeaturesEnabled) {
      if (hasVoiceSIBeenEnabledRef.current) {
        setMicError(VOICE_CONSENT_REQUIRED_MESSAGE);
      }
      return;
    }
    hasVoiceSIBeenEnabledRef.current = true;
    setMicError((current) =>
      current === VOICE_CONSENT_REQUIRED_MESSAGE ? "" : current
    );
  }, [onlineFeaturesEnabled]);

  useEffect(() => {
    micEnabledRef.current = micEnabled;
  }, [micEnabled]);

  useEffect(() => {
    if (!micInputSupported && !isMobile) {
      console.warn("⚠️ Microphone input is not supported in this browser.");
      setMicError("Speech recognition is not supported in this browser.");
      return;
    }

    setMicError((current) =>
      current === "Speech recognition is not supported in this browser." ? "" : current
    );
  }, [isMobile, micInputSupported]);

  useEffect(() => {
    if (isMicrophoneAvailable === false) {
      setMicError("Microphone access is blocked. Allow mic permission and try again.");
    }
  }, [isMicrophoneAvailable]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      Promise.resolve(SpeechRecognition.stopListening?.()).catch(() => {});
      if (nativeRestartTimeoutRef.current) {
        window.clearTimeout(nativeRestartTimeoutRef.current);
      }

      if (nativeRecognitionRef.current) {
        nativeRecognitionRef.current.onend = null;
        nativeRecognitionRef.current.stop?.();
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startMicListening = async ({ userInitiated = false } = {}) => {
    if (!isConsentGranted('voice_actions')) {
      if (userInitiated) setMicError(VOICE_CONSENT_REQUIRED_MESSAGE);
      return false;
    }
    if (!window.isSecureContext && window.location.hostname !== "localhost") {
      setMicError("HTTPS is required for microphone access.");
      return false;
    }

    if (!micInputSupported) {
      setMicError("Microphone input is not available in this browser.");
      return false;
    }

    try {
      let stream = null;

      if (navigator.mediaDevices?.getUserMedia) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }


      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (!mountedRef.current || !isConsentGranted('voice_actions')) {
        return false;
      }

      if (isMobile && nativeSpeechRecognitionSupported) {
        const NativeRecognition =
          window.SpeechRecognition || window.webkitSpeechRecognition;

        if (nativeRestartTimeoutRef.current) {
          window.clearTimeout(nativeRestartTimeoutRef.current);
          nativeRestartTimeoutRef.current = null;
        }

        if (nativeRecognitionRef.current) {
          nativeRecognitionRef.current.onend = null;
          try {
            nativeRecognitionRef.current.stop();
          } catch (stopError) {
            console.warn(
              "🎙️ Native mobile recognizer stop before restart failed:");
          }
          nativeRecognitionRef.current = null;
        }

        if (!NativeRecognition) {
          setMicError("Speech recognition is not supported in this browser.");
          return false;
        }

        const recognition = new NativeRecognition();
        recognition.lang = "en-US";
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
          if(!mountedRef.current||!isConsentGranted('voice_actions')){recognition.stop();return;}
          setNativeListening(true);
          setMicError("");
        };

        recognition.onresult = (event) => {
          if(!mountedRef.current||!isConsentGranted('voice_actions'))return;
          const results = Array.from(event.results || []).slice(event.resultIndex || 0);
          const spoken = results
            .map((result) => result?.[0]?.transcript || "")
            .join(" ")
            .trim();

          if (!spoken) {
            return;
          }

          const immediateCommand = detectImmediateMobileCommand(spoken);
          const latestResult = results[results.length - 1];
          const isFinal = Boolean(latestResult?.isFinal);

          if (immediateCommand) {
            console.log(`🎙️ Native mobile ${isFinal ? "final" : "interim"} command detected.`);
            setNativeTranscript(immediateCommand);
            return;
          }

          if (isFinal) {
            setNativeTranscript(spoken);
          }
        };

        recognition.onerror = (event) => {
          console.error("🎙️ Native mobile speech error:");

          if (event?.error === "not-allowed" || event?.error === "service-not-allowed") {
            setMicError("Microphone access is blocked. Allow mic permission and try again.");
          } else if (
            event?.error &&
            event.error !== "no-speech" &&
            event.error !== "aborted"
          ) {
            setMicError("Unable to start speech recognition on this mobile browser.");
          }
        };

        recognition.onend = () => {
          setNativeListening(false);

          if (!mountedRef.current || !micEnabledRef.current || !isConsentGranted('voice_actions')) {
            return;
          }

          nativeRestartTimeoutRef.current = window.setTimeout(() => {
            if(!mountedRef.current||!micEnabledRef.current||!isConsentGranted('voice_actions'))return;
            try {
              recognition.start();
            } catch (restartError) {
              console.error("🎙️ Failed to restart native mobile speech:");
            }
          }, 80);
        };

        nativeRecognitionRef.current = recognition;
        setNativeTranscript("");
        recognition.start();
        setMicError("");
        return true;
      }

      await SpeechRecognition.startListening({
        continuous: !isMobile && browserSupportsContinuousListening,
        language: "en-US",
      });
      if (!mountedRef.current || !isConsentGranted('voice_actions')) {
        await SpeechRecognition.stopListening();
        return false;
      }
      setMicError("");
      return true;
    } catch (error) {
      console.error("🎙️ Failed to start microphone:");
      setMicError("Unable to start the microphone. Check site permissions.");
      return false;
    }
  };

  const stopMicListening = async () => {
    micEnabledRef.current = false;

    if (nativeRestartTimeoutRef.current) {
      window.clearTimeout(nativeRestartTimeoutRef.current);
      nativeRestartTimeoutRef.current = null;
    }

    if (isMobile && mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      setNativeListening(false);
      mediaRecorderRef.current.stop();

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      return;
    }

    if (isMobile && nativeRecognitionRef.current) {
      const recognition = nativeRecognitionRef.current;
      recognition.onend = null;
      nativeRecognitionRef.current = null;
      recognition.stop();
      setNativeListening(false);
      return;
    }

    await SpeechRecognition.stopListening();
  };

  useEffect(() => {
    if (onlineFeaturesEnabled || !micEnabled) return;
    micEnabledRef.current = false;
    if (nativeRestartTimeoutRef.current) {
      window.clearTimeout(nativeRestartTimeoutRef.current);
      nativeRestartTimeoutRef.current = null;
    }
    if (nativeRecognitionRef.current) {
      nativeRecognitionRef.current.onend = null;
      try { nativeRecognitionRef.current.stop(); } catch {}
      nativeRecognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    Promise.resolve(SpeechRecognition.stopListening?.()).catch(() => {});
    setNativeListening(false);
  }, [onlineFeaturesEnabled, micEnabled, SpeechRecognition]);

  return {
    nativeTranscript,
    setNativeTranscript,
    nativeListening,
    micError,
    micInputSupported,
    startMicListening,
    stopMicListening,
  };
}
